import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { TextField } from '@/components/ui/TextField'
import { useLounge } from '@/features/lounges/api'
import { useCreateNook } from '@/features/nooks/api'
import { errorMessage } from '@/lib/graphql'

/**
 * Create a nook.
 *
 * A route rather than an `isOpen` boolean, same as creating a lounge: it is
 * deep-linkable, survives a refresh, and the back button closes it.
 */
export const Route = createFileRoute('/_app/l/$loungeSlug/nooks/new')({
  component: CreateNookModal,
})

function CreateNookModal() {
  const { loungeSlug } = Route.useParams()
  const navigate = useNavigate()
  const { data } = useLounge(loungeSlug)
  const loungeId = data?.lounge?.id ?? ''
  const createNook = useCreateNook(loungeId)
  const [name, setName] = useState('')

  const close = () => void navigate({ to: '/l/$loungeSlug', params: { loungeSlug } })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    createNook.mutate(name, {
      // Straight into the room that was just made — creating one is almost
      // always the first half of wanting to say something in it.
      onSuccess: (created) => {
        void navigate({
          to: '/l/$loungeSlug/n/$nookId',
          params: { loungeSlug, nookId: created.createNook.id },
        })
      },
    })
  }

  return (
    <Dialog
      open
      onClose={close}
      title="Create a nook"
      description="A nook is a room: a conversation with its own history, and its own voice space when you want one."
    >
      <form onSubmit={submit}>
        <div className="dialog-body">
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="general"
            hint="No categories, no channel types — just a name."
            autoFocus
            required
          />

          {createNook.isError && (
            <p className="form-error" role="alert">
              {errorMessage(createNook.error, 'That nook could not be created.')}
            </p>
          )}
        </div>

        <div className="dialog-actions">
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={createNook.isPending}>
            {createNook.isPending ? 'Creating…' : 'Create nook'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
